<template>
  <div class="setting-body">
    <div class="setting-body-title m-b-20">{{ routerTitle }}</div>
    <div class="expense-billing">
      <el-form :model="filterForm" label-suffix="：" inline size="small">
        <el-form-item style="width: 100%">
          <el-radio-group
            v-model="filterForm.chargeType"
            size="medium"
            @change="changeChargeType"
          >
            <el-radio-button
              v-for="item in feeTypeList"
              :key="item.value"
              :label="item.value"
            >{{ item.label }}</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="!isPersonalPay" label="付款单位">
          <el-select v-model="filterForm.buyerId" style="width: 300px">
            <el-option
              v-for="item in companyList"
              :key="item.companyName"
              :label="item.companyName"
              :value="item.companyId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="支付时间">
          <jczh-date-picker
            v-model="timeRange"
            type="datetimeRange"
            :default-time="['00:00:00', '23:59:59']"
          />
        </el-form-item>

        <template v-if="showProjectFilter">
          <el-form-item label="项目名称">
            <el-input
              v-model.trim="filterForm.projectName"
              placeholder="请输入名称"
            />
          </el-form-item>
          <el-form-item v-if="showCompany" label="收费企业">
            <purchaser-or-agency-async-select v-model="filterForm.sellerId" />
          </el-form-item>
        </template>

        <el-form-item label="发票申请状态">
          <el-select v-model="filterForm.invoiceApplyStatus">
            <el-option label="全部" value="" />
            <el-option
              v-for="item in invoiceApplyStatusDict.dictArray"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <div class="flex">
            <el-button type="primary" size="small" @click="handleSearch">
              查询
            </el-button>
            <el-button size="small" @click="handleReset">
              重置
            </el-button>
          </div>
        </el-form-item>
      </el-form>

      <div class="m-b-20 operate">
        <el-button
          type="primary"
          size="small"
          @click="batchApply"
        >申请开具发票</el-button>
        <el-button
          v-if="showMergeApply"
          type="primary"
          size="small"
          @click="mergeApply"
        >合并开具发票</el-button>
      </div>

      <jczh-table-content
        ref="table"
        :data="tableData"
        :option="tableOption"
        pagination
        :total="total"
        :page.sync="page"
        :limit.sync="size"
        selection
        table-id="id"
        :selectable="showApply"
        @selection-change="handleSelectionChange"
      >
        <template v-slot:last>
          <el-table-column
            label="操作"
            align="center"
            width="80px"
            fixed="right"
          >
            <template v-slot="{ row }">
              <el-button
                v-if="handleApply(row)"
                type="text"
                @click="apply(row)"
              >申请</el-button>
            </template>
          </el-table-column>
        </template>
      </jczh-table-content>

      <apply-invoice-drawer
        :visible.sync="drawerVisible"
        :company-id="filterForm.buyerId"
        :ids="applyIdList"
        :charge-type="filterForm.chargeType"
        :apply-type="applyType"
        :amount="amount"
        :account-balance="accountBalance"
        :is-personal="isPersonal"
        :is-our-company-id="isOurCompanyId"
        @afterConfirm="getListAndClearSelection"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  feeTypeDict,
  payChannelDict,
  invoiceApplyStatusDict,
  invoiceApplyTypeDict,
  payTypeDict,
  orderTypeDict
} from '@/dictionary/financial'
import { invoiceTypeDict } from '@/dictionary/financial'
import ApplyInvoiceDrawer from '@/components/Drawer/ApplyInvoiceDrawer'
import PurchaserOrAgencyAsyncSelect from '@/components/PersonalCenter/PurchaserOrAgencyAsyncSelect'
import { listFilterMixin } from 'jczh-ui'
import { getExpenseBillingApi } from '@/api/financial/financial'
import personalMixin from '@/views/personal-center/personalMixin'
import { strToThousands } from '@/utils/utils'
import InvoiceNoticeDialog from '@/components/InvoiceNoticeDialog.vue'
import { openDialog } from '@/utils/open-dialog'

/**
 * @description 费用账单
 */
export default {
  name: 'ExpenseBilling',

  components: {
    ApplyInvoiceDrawer,
    PurchaserOrAgencyAsyncSelect
  },

  mixins: [listFilterMixin, personalMixin],

  data() {
    return {
      amount: 0,
      accountBalance: 0,
      isPersonal: false,
      feeTypeDict,
      payChannelDict,
      invoiceApplyStatusDict,
      invoiceTypeDict,
      filterForm: {
        // 买方公司id
        buyerId: '',
        // 收费类型
        chargeType: feeTypeDict.SIGN_UP_PAY.value,
        // 项目名称
        projectName: '',
        // 支付开始时间
        beginPayTime: '',
        // 支付结束时间
        endPayTime: '',
        // 发票申请状态
        invoiceApplyStatus: '',
        // 卖方公司id
        sellerId: '',
        // 订单号
        paymentNo: '',
        // 支付类型
        orderType: orderTypeDict.COMPANY.value
      },
      tableData: [],
      total: 0,
      tableOption: [
        {
          label: '收费类型',
          prop: 'chargeTypeName',
          minWidth: '200'
        },
        {
          label: '付款单位',
          prop: 'buyerName',
          minWidth: '200'
        },
        {
          label: '付款单位类型',
          prop: 'orderTypeValue',
          minWidth: '120'
        },
        {
          label: '项目名称',
          prop: 'projectName',
          minWidth: 160
        },
        {
          label: '收费企业',
          prop: 'sellerCompanyName',
          minWidth: 140
        },
        {
          label: '支付渠道',
          prop: 'payChannel'
        },
        {
          label: '支付方式',
          prop: 'payType',
          minWidth: 100,
          filter: value => payTypeDict.translate(value).label || '-'
        },
        {
          label: '支付时间',
          prop: 'payTime',
          minWidth: 160
        },
        {
          label: '支付金额（元）',
          prop: 'amount',
          minWidth: 120,
          filter: value => strToThousands(value)
        },
        {
          label: '发票申请状态',
          prop: 'invoiceApplyStatus',
          minWidth: 120,
          tagDict: invoiceApplyStatusDict
        },
        {
          label: '交易流水号',
          prop: 'paymentNo',
          minWidth: 220
        },
        // 用于显示线下退款的订单
        {
          label: '备注',
          prop: 'refundStatus'
        }
      ],
      // 申请的订单id列表
      applyIdList: [],
      // 已选订单
      selectionRow: [],
      // 开票类别
      applyType: invoiceApplyTypeDict.SINGLE_APPLY.value,
      drawerVisible: false,

      // 是否本公司
      isOurCompanyId: 0
    }
  },

  computed: {
    ...mapState('user', ['companyList', 'userId', 'employeeId']),
    ...mapState('company', ['companyId']),

    timeRange: {
      get() {
        return [this.filterForm.beginPayTime, this.filterForm.endPayTime]
      },
      set(val) {
        if (val?.length) {
          this.filterForm.beginPayTime = val[0]
          this.filterForm.endPayTime = val[1]
        }
      }
    },

    // 未申请和已拒绝时显示申请操作
    showApply() {
      const { NOT_APPLY, REJECTED } = invoiceApplyStatusDict
      return row =>
        (row.invoiceApplyStatus === NOT_APPLY.value ||
          row.invoiceApplyStatus === REJECTED.value) &&
        Number(row.accountBalance) !== 0
    },

    // 收费企业搜索项展示条件
    showCompany() {
      return (
        this.filterForm.chargeType !== feeTypeDict.AGENT_SERVICE_FEE_PAY.value
      )
    },

    // 显示项目名称和收费企业搜索条件
    showProjectFilter() {
      return (
        this.filterForm.chargeType !== feeTypeDict.CA_PAY.value &&
        this.filterForm.chargeType !== feeTypeDict.VAS.value
      )
    },

    // 是否显示合并开票
    showMergeApply() {
      // 平台增值服务费和平台CA不支持合并开票，不显示按钮
      return (
        this.filterForm.chargeType !== feeTypeDict.CA_PAY.value &&
        this.filterForm.chargeType !== feeTypeDict.VAS.value
      )
    },

    // 是否是个人费用
    isPersonalPay() {
      return this.filterForm.chargeType === feeTypeDict.VAS.value
    },

    // 费用类型列表
    feeTypeList() {
      return feeTypeDict.array || []
    }
  },

  watch: {
    companyId: {
      handler(val) {
        this.filterForm.buyerId = val
        this.defineFormReset()
        this.handleSearch()
      },
      immediate: true
    },

    employeeId(val) {
      if (val) {
        this.isPersonal = false
        this.filterForm.orderType = 0
      } else {
        this.isPersonal = true
        this.filterForm.orderType = 1
      }
    },

    companyList: {
      handler(val) {
        // 如果是个人账户登录，默认选择已加入企业列表中的第一家企业
        if (!this.filterForm.buyerId) {
          this.filterForm.buyerId = val[0]?.companyId || ''
        }
      },
      immediate: true,
      deep: true
    }
  },

  mounted() {
    if (this.companyId) {
      this.filterForm.buyerId = this.companyId
      this.defineFormReset()
    }

    this.$store.dispatch('user/getJoinedCompany')
  },

  methods: {
    /**
     * 修改支付类型
     */
    changeChargeType() {
      // 如果当前的收费类型是平台CA和平台增值服务，则清空项目名称和收费企业
      if (
        this.filterForm.chargeType === feeTypeDict.CA_PAY.value ||
        this.filterForm.chargeType === feeTypeDict.VAS.value
      ) {
        this.filterForm.projectName = ''
        this.filterForm.sellerId = ''
      }
      this.handleSearch()
    },

    // 申请开票
    apply(row) {
      if (this.openInvoiceNoticeDialog()) return

      this.drawerVisible = true
      this.applyType = invoiceApplyTypeDict.SINGLE_APPLY.value
      this.applyIdList = []
      this.applyIdList.push(row.id)
      this.amount = Number(row.amount) || 0
      this.accountBalance = Number(row.accountBalance) || 0
      this.isOurCompanyId = Number(row.isOurCompanyId) || 0
    },

    handleSelectionChange(selection) {
      this.selectionRow = selection
    },

    // 批量申请
    batchApply() {
      if (this.openInvoiceNoticeDialog()) return

      if (this.selectionRow.length === 0) {
        this.$message.warning('请勾选要开具发票的账单')
        return
      }

      // 检查已选账单的收费企业是否相同
      const sameSeller = this.selectionRow.every(
        value =>
          value.sellerCompanyName === this.selectionRow[0].sellerCompanyName
      )
      if (!sameSeller) {
        this.$message.warning('请选择相同的收费企业')
        return
      }

      this.applyType = invoiceApplyTypeDict.SINGLE_APPLY.value
      console.log('selectionRow', this.selectionRow)
      this.applyIdList = this.selectionRow.map(row => row.id)
      this.isOurCompanyId = Number(this.selectionRow[0].isOurCompanyId) || 0
      this.drawerVisible = true
    },

    // 合并开票
    mergeApply() {
      if (this.openInvoiceNoticeDialog()) return

      // 检查是否已选账单
      if (this.selectionRow.length === 0) {
        this.$message.warning('请勾选要合并开具发票的账单')
        return
      }

      // 检查已选账单数量是否大于1
      if (this.selectionRow.length <= 1) {
        this.$message.warning('请勾选多张账单进行合并开票')
        return
      }

      // 检查已选账单的收费企业是否相同
      const sameSeller = this.selectionRow.every(
        value =>
          value.sellerCompanyName === this.selectionRow[0].sellerCompanyName
      )
      if (!sameSeller) {
        this.$message.warning('请选择相同的收费企业')
        return
      }

      // 传递数据，调用申请抽屉
      this.applyType = invoiceApplyTypeDict.COMBINE_APPLY.value
      this.applyIdList = this.selectionRow.map(row => row.id)
      this.isOurCompanyId = Number(this.selectionRow[0].isOurCompanyId) || 0
      this.drawerVisible = true
    },

    /**
     * 重写reset方法
     */
    handleReset() {
      this.reset()
      // 如果是个人账户登录，则重置时默认选择第一家企业
      if (!this.employeeId && !this.filterForm.buyerId) {
        this.filterForm.buyerId = this.companyList[0]?.companyId || ''
      }
      this.handleSearch()
    },

    getList() {
      this.epitaxy()

      // 如果查询的费用类型为增值服务，则清除buyerId
      if (this.filterForm.chargeType === feeTypeDict.VAS.value) {
        this.filterForm.buyerId = ''
      }

      const requestParams = Object.assign(
        {},
        {
          pageNum: this.page,
          pageSize: this.size
        },
        this.filterForm
      )

      getExpenseBillingApi(requestParams)
        .then(res => {
          this.tableData = res.data?.records || []
          this.total = res.data?.total || 0
        })
        .catch(err => {
          if (err?.data) {
            this.$message.error(err?.data?.message || '获取费用账单失败')
          }
        })
    },

    getListAndClearSelection() {
      this.$refs.table.clearSelection()
      this.getList()
    },

    handleDrawerClose() {
      this.$refs?.generalInvoice?.cancel()
      this.$refs?.specialInvoice?.cancel()
    },

    // 确认申请
    async confirmApply() {
      try {
        await this.$confirm('是否申请开票', '系统提示', { type: 'warning' })
        this.drawerVisible = false
      } catch (err) {
        if (err?.data) {
          this.$message.error(err?.data?.message || '申请开票失败')
        }
      }
    },

    handleApply(row) {
      // 如果备注信息已退款或实付金额为0，则不显示申请操作
      if (row.refundStatus === '退款成功' || !Number(row.accountBalance)) {
        return false
      }

      // 未申请和已拒绝时显示申请操作
      const { NOT_APPLY, REJECTED } = invoiceApplyStatusDict
      return (
        row.invoiceApplyStatus === NOT_APPLY.value ||
        row.invoiceApplyStatus === REJECTED.value
      )
    },

    /**
     * 开票公告提示
     */
    openInvoiceNoticeDialog() {
      // 关闭公告，开放申请开票功能
      const showNotice = false
      if (showNotice) {
        openDialog({
          component: InvoiceNoticeDialog
        })
      }
      return showNotice
    }
  }
}
</script>

<style lang="scss" scoped>
.setting-body {
  width: 1030px;
  padding: 20px;
  background-color: $white-color;
  overflow: hidden;
  position: relative;
  border-radius: 4px;

  .setting-body-title {
    text-align: left;
    font-size: 20px;
    &::before {
      content: '';
      display: inline-block;
      width: 3px;
      height: 25px;
      background-color: $primary-color;
      vertical-align: bottom;
      margin-right: 10px;
    }
  }
}

.expense-billing {
  ::v-deep .el-form {
    text-align: left;

    .el-form-item {
      margin-bottom: 1rem;
    }
  }

  .operate {
    text-align: left;
  }

  .merge-apply-tips {
    color: $text-third-color;
  }
}

.tip-list {
  padding: 8px 16px;
  background-color: $info-sixth-color;
  border: 1px solid $info-fourth-color;
  border-radius: 4px;
  margin-top: 16px;
  line-height: 1.5;

  ol {
    margin: 0;
    padding: 0 0 0 18px;
  }

  .tip-important {
    color: $danger-color;
  }
}
</style>
